import { ValidationErrorType, type FormManagerActions, type FormSnapshot } from "@ngt-form/core";

import type { TMDValidationError } from "src/types/TMDValidationError";

/**
 * Allows submission only when no form or field error has severity greater than Warning.
 *
 * Preconditions: The snapshot supplies its form-error array and field-error collections.
 * Postconditions: Returns a decision without changing errors or consulting blocking metadata.
 */
const allowSubmit = (
    snapshot: FormSnapshot<any, TMDValidationError, any>,
    _formManager: FormManagerActions<any, TMDValidationError, any>
) => {
    const hasFormErrors =
        snapshot.formErrors.length > 0 &&
        snapshot.formErrors.some(x => x.type > ValidationErrorType.Warning);

    if (hasFormErrors) {
        return false;
    }

    const hasFieldErrors =
        snapshot.fieldErrors &&
        Object.keys(snapshot.fieldErrors).some(propertyPath => {
            return (
                snapshot.fieldErrors[propertyPath] &&
                snapshot.fieldErrors[propertyPath].length > 0 &&
                snapshot.fieldErrors[propertyPath].some(x => x.type > ValidationErrorType.Warning)
            );
        });

    return !hasFieldErrors;
};

/** Returns the shared stateless severity-based submit policy without allocating a new callback. */
const useTMDFormAllowSubmit = <
    TFormValue extends object = object,
    TFormMetadata extends object = object
>() => {
    return allowSubmit;
};

export default useTMDFormAllowSubmit;
